import useVisibleColumns from "../../hooks/useVisibleColumns";
import { Delivery, Driver, FullDeliveryStatusId } from "../../types";
import { driversConfig as config } from "./driversConfig.tsx";
import { useEffect, useMemo } from "react";
import { FilterService } from "primereact/api";
import { ConfirmDialog } from "primereact/confirmdialog";
import { DriversTable } from "./components/DriversTable";
import DriversToolbar from "./DriversToolbar";
import { ViewProvider } from "../../contexts/ViewContext";
import { api } from "../../api";
import { driversFromExternalList } from "./converts";
import { useDataGridQuery, useUrlState } from "../../hooks/useUrlState";
import { DRIVERS_DEFAULTS } from "../../helpers/defaultUrl";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { extractApiError } from "../../api/errors";
import { driversSortFieldMap } from "../../helpers/sortFieldMap";
import { TableDialogs } from "../../components/TableDialogs";

import { useShowToast } from "../../contexts/ToastContext.tsx";
import { useSearchParams } from "react-router";
import { parseId } from "../../helpers/parseId.ts";

const columns = config.columns;

export default function DriversPage() {
  const [visibleColumns, setVisibleColumns] = useVisibleColumns(columns);
  const showToast = useShowToast();

  const { globalFilterValue, setGlobalFilterValue } = useUrlState();
  const debouncedSearchValue = useDebouncedValue(globalFilterValue, 300);

  const { page, limit, sort, order, onPageChange, onSortChange } =
    useDataGridQuery({
      page: DRIVERS_DEFAULTS.page,
      limit: DRIVERS_DEFAULTS.limit,
      sort: DRIVERS_DEFAULTS.sort,
      order: DRIVERS_DEFAULTS.order,
    });

  const [searchParams] = useSearchParams();

  //sorting
  const orderByField = useMemo(() => {
    const mapped = driversSortFieldMap[sort];
    const base = mapped ?? DRIVERS_DEFAULTS.sort;
    const direction = order === -1 ? "-" : "";
    return `${direction}${base}`;
  }, [sort, order]);

  const {
    data: rawData,
    isLoading,
    error,
  } = api.endpoints.listDriversApiDriversGet.useQuery({
    orderBy: `${orderByField},-created_at`, // If two have same unloading date, let's still have some predictable order for them
    limit: limit,
    offset: page * limit,
    includeOrders: true,
    search: debouncedSearchValue || undefined,
    managerId: parseId(searchParams.get("managerId")) || undefined,
  });

  const data: Driver[] = useMemo(() => {
    if (!rawData) {
      return [];
    }
    return rawData.items.map(driversFromExternalList);
  }, [rawData]);

  const { data: vehicles } = api.endpoints.listVehiclesApiVehiclesGet.useQuery({
    limit: 10000,
    orderBy: "number",
  });

  const { data: transporters } =
    api.endpoints.listCompaniesApiCompaniesGet.useQuery({
      limit: 10000,
      isTransporter: true,
      orderBy: "name",
    });

  const { data: organizationalUsers } =
    api.endpoints.getOrganizationalUsersApiOrganizationalUsersGet.useQuery({
      limit: 10000,
    });

  const dropdownConfigs = useMemo(() => {
    return {
      trucks:
        vehicles?.items
          ?.filter((vehicle) => vehicle.type === "truck")
          .map((vehicle) => ({
            label: vehicle.number,
            value: vehicle.id,
            data: vehicle,
          })) ?? [],
      trailers:
        vehicles?.items
          ?.filter((vehicle) => vehicle.type === "trailer")
          .map((vehicle) => ({
            label: vehicle.number,
            value: vehicle.id,
            data: vehicle,
          })) ?? [],
      transporters:
        transporters?.items?.map((company) => ({
          label: company.name,
          value: company.id,
        })) ?? [],
      organizationalUsers:
        organizationalUsers?.items?.map((user) => ({
          label: user.email,
          value: user.id,
        })) ?? [],
    };
  }, [vehicles, transporters, organizationalUsers]);

  //CUSTOM FILTER FOR SCHEDULED DELIVERY
  useEffect(() => {
    FilterService.register(
      "custom_scheduledDeliveries",
      (value: Delivery[], filter: [Date | null, Date | null]) => {
        const [start, end] = Array.isArray(filter) ? filter : [null, null];
        if (!Array.isArray(value)) return false;

        const excludedStatuses: FullDeliveryStatusId[] = [
          "CONFIRMED",
          "CANCELLED",
        ];

        return value.some((delivery) => {
          if (!delivery.date || excludedStatuses.includes(delivery.status))
            return false;

          const time = new Date(delivery.date).getTime();
          const startTime = start ? new Date(start).getTime() : null;
          const endTime = end ? new Date(end).getTime() : null;

          return (
            (!startTime || time >= startTime) && (!endTime || time <= endTime)
          );
        });
      },
    );
  }, []);

  useEffect(() => {
    if (error) {
      showToast({
        severity: "error",
        summary: "Błąd ładowania danych",
        detail: extractApiError(error),
      });
    }
  }, [error, showToast]);

  return (
    <ViewProvider view={config.view}>
      <ConfirmDialog />
      <TableDialogs />
      <DriversToolbar
        config={config}
        globalFilterValue={globalFilterValue}
        setGlobalFilterValue={setGlobalFilterValue}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        // setFilters={setDateRange}
        dropdownConfigs={dropdownConfigs}
      />
      <DriversTable
        config={config}
        data={data}
        page={page}
        onPage={(e) => onPageChange(e.page ?? 0, e.rows ?? limit)}
        rowsPerPage={limit}
        totalRecords={rawData?.total || 0}
        sortField={sort}
        sortOrder={order}
        onSort={(e) => onSortChange(e.sortField, e.sortOrder ?? 1)}
        visibleColumns={visibleColumns}
        isLoading={isLoading}
        globalFilterValue={globalFilterValue}
        dropdownConfigs={dropdownConfigs}
        // filters={filters}
      />
    </ViewProvider>
  );
}
